import { useEffect, useState } from "react";
import { fetchSosApi } from "api/sos";
import { ISos, ISosReason, ISosUser } from "models/sos";
import { useNotificationContext } from "contexts/NotificationContext";
import PreviousIcon from "components/Icons/PreviousIcon";
import NextIcon from "components/Icons/NextIcon";
import { Avatar, Table, TableColumnsType, TablePaginationConfig } from "antd";
import { defaultTableData, ITableData } from "models";
import moment from "moment";
import Status from "./components/Status";
import Seen from "./components/Seen";
import { AlertContextProvider } from "./components/AlertContext";
import Action from "./components/Action";
import { useDataLoadingContext } from "contexts/DataLoadingContext";

export default function Sos() {
  const { showError } = useNotificationContext();
  const { reloadSos } = useDataLoadingContext();
  const [data, setData] = useState<ITableData<ISos>>(defaultTableData);

  useEffect(() => {
    fetchSos();
  }, [data.page, reloadSos]); // eslint-disable-line

  const fetchSos = async () => {
    setData((prev) => ({ ...prev, loading: true }));
    const { data: response, error } = await fetchSosApi(
      data.page,
      data.per_page
    );
    if (response) {
      response.data = response.data.map((value) => ({
        ...value,
        key: value.id,
      }));
      setData((prev) => ({ ...prev, ...response, loading: false }));
    } else {
      showError(error!.message);
    }
  };

  const handlePageChange = ({ current }: TablePaginationConfig) => {
    if (current) setData((prev) => ({ ...prev, page: current }));
  };

  return (
    <AlertContextProvider>
      <div className="w-full p-2 h-[calc(100vh-72px)]">
        <div className="w-full">
          <div className="w-full px-6 flex flex-wrap-reverse"></div>
          <div className="w-full mt-8 px-4">
            <Table
              loading={data.loading}
              columns={columns}
              dataSource={data.data}
              onChange={handlePageChange}
              pagination={{
                total: data.total,
                pageSize: data.per_page,
                current: data.page,
                nextIcon: (
                  <span className="flex items-center justify-center w-8 h-8 text-2xl">
                    <NextIcon />
                  </span>
                ),
                prevIcon: (
                  <span className="flex items-center justify-center w-8 h-8 text-2xl">
                    <PreviousIcon />
                  </span>
                ),
              }}
            />
          </div>
        </div>
      </div>
    </AlertContextProvider>
  );
}

const columns: TableColumnsType<ISos> = [
  {
    title: "Image",
    dataIndex: "user",
    render: (user: ISosUser) => (
      <div className="flex items-center gap-y-1 gap-x-1">
        <div className="w-full max-w-10 h-10">
          <Avatar
            shape="circle"
            size={40}
            src={user.image}
            className="max-w-10"
          />
        </div>
      </div>
    ),
  },
  {
    title: "Full Name",
    dataIndex: "user",
    render: (user: ISosUser) => (
      <div className="flex items-center gap-y-1 gap-x-1">
        <span className="whitespace-pre-line">
          {user ? `${user.first_name} ${user.last_name}` : "No user"}
        </span>
      </div>
    ),
  },
  {
    title: "User Type",
    dataIndex: "type",
    render: (type: string) => (
      <div className="flex items-center gap-y-1 gap-x-1">
        <span className="whitespace-pre-line">
          {`${type === "driver" ? "Driver" : "Passenger"}`}
        </span>
      </div>
    ),
  },
  {
    title: "Initiated Date",
    dataIndex: "initiated_at",
    render: (started_at) => (
      <span className="text-nowrap">{moment(started_at).format("lll")}</span>
    ),
  },
  {
    title: "Reason",
    dataIndex: "reason",
    render: (value: ISosReason) => (
      <p className="text-center capitalize">
        {value === "manual"
          ? "normal sos"
          : value === "location_off"
          ? "in trip location disabled "
          : "developer mode on "}
      </p>
    ),
  },
  {
    title: "Seen",
    dataIndex: "seen",
    render: Seen,
  },
  {
    title: "Status",
    dataIndex: "status",
    render: Status,
  },
  {
    title: "Deactivated At",
    dataIndex: "deactivated_at",
    render: (deactivated_by_account_id) => (
      <span className="text-nowrap">
        {deactivated_by_account_id
          ? moment(deactivated_by_account_id).format("lll")
          : ""}
      </span>
    ),
  },
  {
    title: "Action",
    render: (sos: ISos) => <Action sos={sos} />,
  },
];
